import { useState, useCallback } from 'react';

const emptyEntry = () => ({
  genderIdentity: null, // null = unchanged
  isAlive: null,
  dob: null,
  dod: null,
  firstName: null,
  lastName: null,
  ethnicity: null,
  mobile: null,
  email: null,
  hospitalNumber: null,
  healthcareId: null,
  isHide: null,
  recordsReviewed: null,
  newDiseases: [],
  removedDiseaseIds: [],
  newPhenotypes: [],
  removedPhenotypeIds: [],
  newGenotypes: [],
  removedGenotypeIds: []
});

export function useBulkEditState() {
  const [draft, setDraft] = useState(new Map());

  const getEntry = useCallback(
    (pid) => draft.get(pid) || emptyEntry(),
    [draft]
  );

  const isDirty = useCallback(
    (pid) => {
      const d = draft.get(pid);
      if (!d) return false;
      return (
        d.genderIdentity !== null ||
        d.isAlive !== null ||
        d.dob !== null ||
        d.dod !== null ||
        d.firstName !== null ||
        d.lastName !== null ||
        d.ethnicity !== null ||
        d.mobile !== null ||
        d.email !== null ||
        d.hospitalNumber !== null ||
        d.healthcareId !== null ||
        d.isHide !== null ||
        d.recordsReviewed !== null ||
        d.newDiseases.length > 0 ||
        d.removedDiseaseIds.length > 0 ||
        d.newPhenotypes.length > 0 ||
        d.removedPhenotypeIds.length > 0 ||
        d.newGenotypes.length > 0 ||
        d.removedGenotypeIds.length > 0
      );
    },
    [draft]
  );

  const dirtyCount = useCallback(
    (memberRecords) => memberRecords.filter((m) => isDirty(m.pid)).length,
    [isDirty]
  );

  const patch = useCallback((pid, fields) => {
    setDraft((prev) => {
      const next = new Map(prev);
      next.set(pid, { ...(next.get(pid) || emptyEntry()), ...fields });
      return next;
    });
  }, []);

  const updatePersonField = useCallback(
    (pid, field, value) => patch(pid, { [field]: value }),
    [patch]
  );

  // ── Diseases ──────────────────────────────────────────────────────────────

  const addDisease = useCallback((pid, disease) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.newDiseases.some((d) => d.id === disease.id)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newDiseases: [...entry.newDiseases, disease]
      });
      return next;
    });
  }, []);

  const removeNewDisease = useCallback((pid, diseaseId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newDiseases: entry.newDiseases.filter((d) => d.id !== diseaseId)
      });
      return next;
    });
  }, []);

  // _id is the MongoDB id used for delete
  const markDiseaseRemoved = useCallback((pid, mongoId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.removedDiseaseIds.includes(mongoId)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedDiseaseIds: [...entry.removedDiseaseIds, mongoId]
      });
      return next;
    });
  }, []);

  const unmarkDiseaseRemoved = useCallback((pid, mongoId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedDiseaseIds: entry.removedDiseaseIds.filter(
          (id) => id !== mongoId
        )
      });
      return next;
    });
  }, []);

  // ── Phenotypes ────────────────────────────────────────────────────────────

  const addPhenotype = useCallback((pid, phenotype) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.newPhenotypes.some((p) => p.id === phenotype.id)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newPhenotypes: [...entry.newPhenotypes, phenotype]
      });
      return next;
    });
  }, []);

  const removeNewPhenotype = useCallback((pid, phenotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newPhenotypes: entry.newPhenotypes.filter((p) => p.id !== phenotypeId)
      });
      return next;
    });
  }, []);

  const markPhenotypeRemoved = useCallback((pid, phenotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.removedPhenotypeIds.includes(phenotypeId)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedPhenotypeIds: [...entry.removedPhenotypeIds, phenotypeId]
      });
      return next;
    });
  }, []);

  // ── Genotypes ─────────────────────────────────────────────────────────────

  const addGenotype = useCallback((pid, genotype) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.newGenotypes.some((g) => g.id === genotype.id)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newGenotypes: [...entry.newGenotypes, genotype]
      });
      return next;
    });
  }, []);

  const removeNewGenotype = useCallback((pid, genotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        newGenotypes: entry.newGenotypes.filter((g) => g.id !== genotypeId)
      });
      return next;
    });
  }, []);

  const markGenotypeRemoved = useCallback((pid, genotypeId) => {
    setDraft((prev) => {
      const entry = prev.get(pid) || emptyEntry();
      if (entry.removedGenotypeIds.includes(genotypeId)) return prev;
      const next = new Map(prev);
      next.set(pid, {
        ...entry,
        removedGenotypeIds: [...entry.removedGenotypeIds, genotypeId]
      });
      return next;
    });
  }, []);

  const reset = useCallback(() => setDraft(new Map()), []);

  return {
    draft,
    getEntry,
    isDirty,
    dirtyCount,
    updatePersonField,
    addDisease,
    removeNewDisease,
    markDiseaseRemoved,
    unmarkDiseaseRemoved,
    addPhenotype,
    removeNewPhenotype,
    markPhenotypeRemoved,
    addGenotype,
    removeNewGenotype,
    markGenotypeRemoved,
    reset
  };
}
